import { RaidAttackConfirmationNotice } from './RaidAttackConfirmationNotice.jsx';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { formatRaidHpEok } from '../../../shared/domain/raidHpFormat.js';
import { DororaidIcon } from '../../../shared/ui/DororaidIcon.jsx';
import { StablePortal } from '../../../shared/ui/StablePortal.jsx';
import { RaidPracticeDeckDisplay } from '../../raid-practice/shared/RaidPracticeDeckPicker.jsx';
import { useRaidControlRoom } from '../hooks/useRaidControlRoom.js';
import {
  cancelRaidControlAttackRequest,
  reviewRaidControlAttackRequest,
} from '../api/raidControlRequestApi.js';
import { RaidControlRoom as LegacyRaidControlRoom } from './LegacyRaidControlRoom.jsx';
import { RaidControlMemberAttackBadge } from './RaidControlMemberAttackBadge.jsx';
import { MemberAttackRequestDialog } from './MemberAttackRequestDialog.jsx';
import { OrderDialog } from './RaidControlActionDialogs.jsx';
import { createMobileChatKeyboardGate, isMobileChatKeyboardVisuallyOpen, nextMobileChatKeyboardGate, reduceRaidControlActionState } from './mobileChatInteractionState.js';
import { MobileRaidControlChat } from './MobileRaidControlChat.jsx';
import { RaidControlStrategyPanel } from './RaidControlStrategyPanel.jsx';
import '../../../styles/raid-control-room.css';
import '../../../styles/raid-control-deck-containment.css';
import '../../../styles/raid-control-progress.css';
import '../../../styles/raid-control-mobile-focus.css';
import '../../../styles/raid-control-initial-load.css';
import '../../../styles/raid-control-chat-enhancements.css';
import '../../../styles/raid-control-member-attacks.css';
import '../../../styles/raid-control-regression-fixes.css';
import './raid-control-scrollbars.css';

// One bar in every mobile tab: the menu, the tab's name with a line under it, one button on the right.
const MOBILE_VIEW_TITLES = { chat: '관제 채팅', progress: '진행 중 공격', practice: '멤버 모의전', plan: '계획표' };

const STATUS_META = {
  pending_approval: { label: '승인 대기', priority: 0 },
  attacking: { label: '공격 중', priority: 1 },
  sent: { label: '공격 지시', priority: 2 },
  acknowledged: { label: '공격 지시', priority: 2 },
  held: { label: '보류', priority: 3 },
};
const ATTACK_STALE_MS = 15 * 60_000;

function formatDamage(value) {
  if (value === null || value === undefined || value === '') return '-';
  return `${Number(value || 0).toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억`;
}

function timeLabel(value) {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('ko-KR', { hour: '2-digit', minute: '2-digit' }).format(new Date(value));
  } catch {
    return '';
  }
}

function attackElapsed(value) {
  if (!value) return '';
  const elapsed = Math.max(Date.now() - new Date(value).getTime(), 0);
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return '방금 시작';
  if (minutes < 60) return `${minutes}분 경과`;
  const hours = Math.floor(minutes / 60);
  return `${hours}시간 ${minutes % 60}분 경과`;
}

function isStaleAttack(order) {
  return order?.status === 'attacking' && order.startedAt && Date.now() - new Date(order.startedAt).getTime() >= ATTACK_STALE_MS;
}

function InitialChatScrollGuard({ rootRef, active, resetKey, unreadAnchorId = '' }) {
  useLayoutEffect(() => {
    if (!active || typeof MutationObserver === 'undefined') return undefined;
    let frame = 0;
    let observer = null;

    const prepareMessageList = () => {
      const list = rootRef.current?.querySelector('.raid-control-message-list');
      if (!list) return false;
      list.classList.add('initial-scroll-pending');
      list.classList.remove('initial-scroll-ready');
      const unreadTarget = unreadAnchorId
        ? list.querySelector(`[data-message-id="${unreadAnchorId}"]`)
        : null;
      if (unreadTarget) unreadTarget.scrollIntoView({ block: 'center', behavior: 'auto' });
      else list.scrollTop = list.scrollHeight;
      list.classList.remove('initial-scroll-pending');
      list.classList.add('initial-scroll-ready');
      frame = window.requestAnimationFrame(() => {
        if (unreadTarget) unreadTarget.scrollIntoView({ block: 'center', behavior: 'auto' });
        else list.scrollTop = list.scrollHeight;
      });
      return true;
    };

    if (!prepareMessageList() && rootRef.current) {
      observer = new MutationObserver(() => {
        if (!prepareMessageList()) return;
        observer?.disconnect();
        observer = null;
      });
      observer.observe(rootRef.current, { childList: true, subtree: true });
    }

    return () => {
      observer?.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [active, resetKey, rootRef, unreadAnchorId]);

  return null;
}

function ActiveOrderCard({ order, room, userId, onReview, onCancelRequest }) {
  const isTarget = Boolean(order.targetUserId && order.targetUserId === userId);
  const busy = room.working === `order:${order.id}`;
  const confirmation = ['sent', 'acknowledged', 'held'].includes(order.status) ? room.pendingAttackConfirmations?.[order.id] : null;
  const status = STATUS_META[order.status] || { label: order.status, priority: 9 };
  const deck = order.deck;
  const stale = isStaleAttack(order);
  const runStatus = async (nextStatus) => { await room.changeOrderStatus(order.id, nextStatus); };

  return <article className={`raid-control-progress-card status-${order.status} ${stale ? 'attack-stale' : ''}`.trim()}>
    <header>
      <div><span>{status.label}</span><strong>{order.targetNickname}</strong></div>
      <div className="raid-control-progress-card-where">
        <em>{order.bossName || `${order.bossNo}번 보스`} · {order.phaseNo}단</em>
        {/* The timer lives here rather than on a body row of its own. On that row it had to reserve
            its height before the attack started, so every waiting card carried a blank line. */}
        {order.status === 'attacking' ? (
          <small className={`raid-control-progress-elapsed${stale ? ' stale' : ''}`}>
            {attackElapsed(order.startedAt)}{stale ? ' · 상태 확인 필요' : ''}
          </small>
        ) : null}
      </div>
    </header>
    <div className="raid-control-progress-card-summary">
      <strong>{order.practiceTitle || '지정 덱'}</strong>
      <span>예상 {formatDamage(order.expectedDamageEok)}</span>
    </div>
    {deck?.length ? <RaidPracticeDeckDisplay deck={deck} mobile /> : null}
    {order.note ? <p>{order.note}</p> : null}
    <RaidAttackConfirmationNotice confirmation={confirmation} />
    {room.orderErrors?.[order.id] ? <p className="raid-control-order-error" role="alert">{room.orderErrors[order.id]}</p> : null}
    <div className="raid-control-progress-actions">
      {order.status === 'pending_approval' && room.canDirect ? <>
        <button type="button" className="primary" onClick={() => onReview(order.id, 'approve')}>승인</button>
        <button type="button" className="danger" onClick={() => onReview(order.id, 'reject')}>거절</button>
      </> : null}
      {order.status === 'pending_approval' && isTarget ? <button type="button" onClick={() => onCancelRequest(order.id)}>요청 취소</button> : null}
      {(isTarget || room.canDirect) && ['sent', 'acknowledged', 'held'].includes(order.status) ? <>
        <button disabled={busy} type="button" className="primary" onClick={() => room.changeOrderStatus(order.id, 'attacking', { confirmConcurrent: Boolean(confirmation) })}>{confirmation ? '확인' : '공격 시작'}</button>
        {confirmation ? <button disabled={busy} type="button" onClick={() => room.dismissAttackConfirmation(order.id)}>대기</button> : null}
      </> : null}
      {isTarget && order.status === 'attacking' ? <button disabled={busy} type="button" className="primary" onClick={() => runStatus('completed')}>공격 완료</button> : null}
      {room.canDirect && order.status === 'attacking' ? <button disabled={busy} type="button" onClick={() => runStatus('held')}>상태 해제</button> : null}
      {room.canDirect && order.status !== 'pending_approval' ? <button disabled={busy} type="button" className="danger" onClick={() => runStatus('cancelled')}>지시 취소</button> : null}
    </div>
    <footer className="raid-control-order-time">{timeLabel(order.startedAt || order.createdAt)}{order.createdBy === order.targetUserId ? ' · 멤버 요청' : ' · 관리자 지시'}</footer>
  </article>;
}

export function RaidControlProgressPanel({ room, userId, mobile = false, defaultExpanded = !mobile }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [wide, setWide] = useState(false);
  const panelExpanded = mobile || expanded;
  const activeOrders = useMemo(() => [...room.ordersById.values()]
    .filter((order) => STATUS_META[order.status])
    .sort((left, right) => {
      const statusDiff = STATUS_META[left.status].priority - STATUS_META[right.status].priority;
      if (statusDiff) return statusDiff;
      const bossDiff = Number(left.bossNo) - Number(right.bossNo);
      if (bossDiff) return bossDiff;
      return new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime();
    }), [room.ordersById]);

  const review = async (orderId, decision) => {
    try {
      await reviewRaidControlAttackRequest(orderId, decision);
      await room.refresh({ silent: true });
    } catch (error) {
      window.alert(error instanceof Error ? error.message : '공격 요청을 처리하지 못했습니다.');
    }
  };
  const cancelRequest = async (orderId) => {
    try {
      await cancelRaidControlAttackRequest(orderId);
      await room.refresh({ silent: true });
    } catch (error) {
      window.alert(error instanceof Error ? error.message : '공격 요청을 취소하지 못했습니다.');
    }
  };

  return <aside className={`raid-control-progress-panel ${panelExpanded ? 'expanded' : 'collapsed'} ${mobile ? 'mobile' : 'desktop'}${!mobile && wide ? ' wide' : ''}`} aria-label="진행 중 공격">
    <header>
      {mobile ? <div className="raid-control-progress-toggle">
        <span><strong>진행 중 공격</strong></span><em>{activeOrders.length}건</em>
      </div> : <button type="button" className="raid-control-progress-toggle" onClick={() => setExpanded((value) => !value)} aria-expanded={expanded}>
        <span><strong>진행 중 공격</strong></span><em>{activeOrders.length}건</em>
      </button>}
      {!mobile ? <button type="button" className="raid-control-progress-width-toggle" aria-pressed={wide} aria-label={wide ? '진행 중 공격 기본 보기' : '진행 중 공격 넓게 보기'} title={wide ? '기본 보기' : '넓게 보기'} onClick={() => { setWide(value => !value); setExpanded(true); }}><DororaidIcon name={wide ? 'chevronLeft' : 'chevronRight'} size={20} /></button> : null}
    </header>
    {panelExpanded ? <div className="raid-control-progress-list">
      {activeOrders.length ? activeOrders.map((order) => <ActiveOrderCard key={order.id} order={order} room={room} userId={userId} onReview={review} onCancelRequest={cancelRequest} />) : <div className="raid-control-progress-empty"><strong>현재 진행 중인 공격 없음</strong></div>}
    </div> : null}
  </aside>;
}

function MobileBossStatusPanel({ room, onClose }) {
  const lastSyncAt = room.overview.selectedSeason?.lastSyncAt;
  const stale = lastSyncAt ? Date.now() - new Date(lastSyncAt).getTime() > 5 * 60_000 : true;
  const selectBoss = (bossNo) => {
    room.selectBoss(bossNo);
    onClose();
  };

  return <div className="raid-control-mobile-boss-layer">
    <div className="raid-control-mobile-boss-backdrop" role="presentation" onClick={onClose} />
    <section id="raid-control-mobile-boss-panel" className="raid-control-mobile-boss-panel" role="dialog" aria-modal="true" aria-labelledby="raid-control-mobile-boss-title">
      <header>
        <div><strong id="raid-control-mobile-boss-title">보스 현황</strong></div>
        <span className={stale ? 'stale' : ''}>{stale ? '동기화 확인 필요' : `${timeLabel(lastSyncAt)} 갱신`}</span>
        <button type="button" onClick={onClose} aria-label="보스 현황 닫기"><DororaidIcon name="close" size={20} /></button>
      </header>
      <div className="raid-control-mobile-boss-list">
        {room.bosses.map((boss) => {
          const attackers = room.attackingByBoss.get(boss.bossNo) || [];
          const attackStale = attackers.some(isStaleAttack);
          return <button key={boss.bossNo} type="button" className={attackStale ? 'attack-stale' : ''} onClick={() => selectBoss(boss.bossNo)}>
            <span><strong>{boss.bossName}</strong><small>{boss.currentPhase}단 · {attackers.length ? attackers.map(attacker => `${attacker.targetNickname} 공격 중 · ${attackElapsed(attacker.startedAt)}`).join(' / ') : '공격 대기'}</small></span>
            <em>{boss.isInfinite ? '인피니티' : formatRaidHpEok(boss.remainingHpEok)}</em>
            <i><b style={{ width: `${boss.isInfinite ? 100 : boss.remainingPercent}%` }} /></i>
          </button>;
        })}
      </div>
    </section>
  </div>;
}

export function MobileControlToolbar({ room, unionName = '', activeView, bossPanelOpen, onBossPanelOpen, onBossPanelClose, onSelect, onSelectMember, onExit, portalTarget, detached = false, viewportOwned = false, noticeExpanded = false, onToggleNotice }) {
  const [open, setOpen] = useState(false);
  const drawerRef = useRef(null);
  const onlineMembers = room.onlineMembers || room.members.filter((member) => member.online);
  const offlineMembers = useMemo(() => room.members.filter((member) => !member.online), [room.members]);
  const activeOrderCount = useMemo(() => [...room.ordersById.values()].filter((order) => STATUS_META[order.status]).length, [room.ordersById]);
  const viewMeta = activeView === 'progress'
    ? `${activeOrderCount}건`
    : activeView === 'practice'
      ? `${room.selectedMember?.nickname || '멤버 선택'}${room.selectedMember?.online ? ' · 접속 중' : ''}`
      : '';
  const portalActive = Boolean(
    !detached
    && !viewportOwned
    && activeView === 'chat'
    && portalTarget
    && typeof window !== 'undefined'
    && window.navigator.standalone === true
  );

  useEffect(() => {
    if (!open) return undefined;
    const closeFromEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        onSelect('chat');
      }
    };
    window.addEventListener('keydown', closeFromEscape);
    return () => window.removeEventListener('keydown', closeFromEscape);
  }, [open, onSelect]);

  const closeToChat = () => {
    setOpen(false);
    onSelect('chat');
  };

  const choose = (view) => {
    setOpen(false);
    onSelect(view);
  };

  const openMenu = () => {
    onBossPanelClose();
    setOpen(true);
  };

  const selectMemberPractice = (member) => {
    setOpen(false);
    onSelectMember(member);
  };

  const memberRow = (member, online) => <button type="button" key={member.memberOpenid || member.userId} disabled={!member.memberOpenid} onClick={() => selectMemberPractice(member)}>
    <span className="raid-control-mobile-drawer-avatar">{member.avatarUrl ? <img src={member.avatarUrl} alt="" /> : member.nickname.slice(0, 1)}</span>
    <span><span className="raid-control-member-name"><strong>{member.nickname}</strong><RaidControlMemberAttackBadge member={member} /></span><small>{member.syncLevel ? `Lv.${member.syncLevel}` : member.memberOpenid ? '레벨 미확인' : '명단 미연결'}</small></span>
    <i className={online ? 'online' : ''} />
  </button>;

  const toolbar = <div
    className={`raid-control-mobile-toolbar${portalActive ? ' ios-standalone-portal' : ''}${detached ? ' chat-v2-detached' : ''}${viewportOwned ? ' chat-viewport-owned' : ''}`}
    data-toolbar-owner={viewportOwned ? 'mobile-chat-viewport' : (detached ? 'body-detached-static' : (portalActive ? 'mobile-main' : 'workspace'))}
    style={portalActive ? { top: 'calc(env(safe-area-inset-top) + 8px)' } : undefined}
  >
    <button type="button" className="raid-control-mobile-menu-trigger" aria-expanded={open} aria-controls="raid-control-mobile-drawer" aria-label="관제 메뉴 열기" onClick={openMenu}>
      <span className="raid-control-mobile-menu-glyph" aria-hidden="true"><i /><i /><i /></span>
    </button>
    {activeView === 'chat' ? <button type="button" className="raid-control-mobile-view-title raid-control-mobile-notice-toggle" aria-expanded={noticeExpanded} aria-controls="raid-control-mobile-notice-panel" onClick={() => { onBossPanelClose(); onToggleNotice?.(); }}>
      <strong>{room.chatHeader?.notice ? '공지' : room.chatHeader?.pinnedMessage ? '고정' : '관제 채팅'}</strong>
      <small>{room.chatHeader?.notice || room.chatHeader?.pinnedMessage?.body || (room.chatHeader?.pinnedMessage ? '콘·공유 메시지' : room.canDirect ? '공지 작성 · 메시지 고정' : '공지 없음')}</small>
      <DororaidIcon name={noticeExpanded ? 'chevronUp' : 'chevronDown'} size={16} />
    </button> : <>
    <span className="raid-control-mobile-view-title">
      <strong>{MOBILE_VIEW_TITLES[activeView] || '레이드 관제'}</strong>
      {activeView !== 'chat' && viewMeta ? <small>{viewMeta}</small> : null}
    </span>
    </>}
    {activeView === 'chat' ? <button type="button" className="raid-control-mobile-boss-open" aria-expanded={bossPanelOpen} aria-controls="raid-control-mobile-boss-panel" onClick={bossPanelOpen ? onBossPanelClose : onBossPanelOpen}>
      <span>보스</span><strong>{room.bosses.length}</strong>
    </button> : <button type="button" className="raid-control-mobile-view-close" aria-label="채팅으로" title="채팅으로" onClick={closeToChat}><DororaidIcon name="close" size={20} /></button>}
    {open ? <div className="raid-control-mobile-drawer-layer">
      <div className="raid-control-mobile-drawer-backdrop" role="presentation" onClick={closeToChat} />
      <aside className="raid-control-mobile-drawer" id="raid-control-mobile-drawer" ref={drawerRef} aria-label="레이드 관제 메뉴">
        <header>
          <div>
            {/* The room context carries no union name; the shell passes the one its top bar shows. */}
            <strong>{unionName || '내 유니온'}</strong>
            <span className="raid-control-mobile-drawer-badges"><b>한섭</b><em><DororaidIcon name="members" size={14} />{room.members.length}</em></span>
          </div>
          <button type="button" onClick={closeToChat} aria-label="메뉴 닫기"><DororaidIcon name="close" size={22} /></button>
        </header>
        <div className="raid-control-mobile-drawer-scroll">
          <section className="raid-control-mobile-drawer-section channels">
            <h2>관제 메뉴</h2>
            <button type="button" className={activeView === 'chat' ? 'active' : ''} aria-current={activeView === 'chat' ? 'page' : undefined} onClick={closeToChat}><DororaidIcon name="chat" size={20} /><span><strong>채팅</strong><small>실시간 관제 대화</small></span></button>
            <button type="button" className={activeView === 'plan' ? 'active' : ''} aria-current={activeView === 'plan' ? 'page' : undefined} onClick={() => choose('plan')}><DororaidIcon name="plan" size={20} /><span><strong>계획표</strong><small>단수별 5보스 계획 편집</small></span></button>
            <button type="button" className={activeView === 'progress' ? 'active' : ''} aria-current={activeView === 'progress' ? 'page' : undefined} onClick={() => choose('progress')}><DororaidIcon name="raid" size={20} /><span><strong>진행 중 공격</strong><small>공격 지시와 승인 요청</small></span></button>
            <button type="button" className={activeView === 'practice' ? 'active' : ''} aria-current={activeView === 'practice' ? 'page' : undefined} onClick={() => choose('practice')}><DororaidIcon name="practice" size={20} /><span><strong>멤버 모의전</strong><small>선택 멤버의 보스별 슬롯</small></span></button>
          </section>
          <section className="raid-control-mobile-drawer-section members">
            <h2 className="raid-control-mobile-member-heading">
              <span><DororaidIcon name="members" size={14} />접속 중 · {onlineMembers.length}</span>
              <span className="raid-control-mobile-online-avatars" aria-hidden="true">{onlineMembers.slice(0, 4).map((member) => <i key={member.memberOpenid || member.userId}>{member.avatarUrl ? <img src={member.avatarUrl} alt="" /> : member.nickname.slice(0, 1)}</i>)}</span>
            </h2>
            {room.presenceError ? <p role="status">{room.presenceError}</p> : null}
            {onlineMembers.length ? onlineMembers.map((member) => memberRow(member, true)) : <p>{room.presenceError ? '접속 현황 확인 필요' : room.presenceStatus && room.presenceStatus !== 'connected' ? '접속 현황 연결 중…' : '현재 접속 중인 멤버가 없습니다.'}</p>}
            <h2 className="offline-heading">오프라인 · {offlineMembers.length}</h2>
            {offlineMembers.map((member) => memberRow(member, false))}
          </section>
        </div>
        <footer><button type="button" onClick={onExit}><DororaidIcon name="logout" size={20} /><span>나가기</span></button></footer>
      </aside>
    </div> : null}
  </div>;

  if (viewportOwned) return toolbar;
  const detachedTarget = detached && typeof document !== 'undefined' ? document.body : portalTarget;
  return <StablePortal active={detached || portalActive} target={detachedTarget}>{toolbar}</StablePortal>;
}

const FRESH_CHAT_INPUT_SELECTOR = '.doro-mobile-chat__composer textarea';

function MobileChatViewportOwner({ toolbar = null, overlay = null, children = null }) {
  const shellRef = useRef(null);
  const gateRef = useRef(createMobileChatKeyboardGate());
  const settleVersionRef = useRef(0);
  const frameRef = useRef(0);
  const initialKeyboardState = {
    keyboardOpen: false,
    keyboardVisualOpen: false,
    viewportSettled: false,
    settleVersion: 0,
    keyboardPhase: 'closed',
  };
  const keyboardStateRef = useRef(initialKeyboardState);
  const [keyboardState, setKeyboardState] = useState(initialKeyboardState);

  useLayoutEffect(() => {
    const visualViewport = window.visualViewport;

    const sampleViewport = () => {
      const height = Math.max(1, Number(visualViewport?.height || window.innerHeight || 0));
      const top = Math.max(0, Number(visualViewport?.offsetTop || 0));
      const active = document.activeElement;
      const composerFocused = active instanceof HTMLElement && active.matches(FRESH_CHAT_INPUT_SELECTOR);
      return { height, top, composerFocused };
    };

    const syncKeyboardState = () => {
      frameRef.current = 0;
      const { height, top, composerFocused } = sampleViewport();
      const previousGate = gateRef.current;
      const nextGate = nextMobileChatKeyboardGate(previousGate, { height, composerFocused });
      const keyboardVisualOpen = isMobileChatKeyboardVisuallyOpen({
        height,
        closedHeight: nextGate.closedHeight,
      });
      gateRef.current = nextGate;

      const shellNode = shellRef.current;
      if (shellNode) {
        shellNode.dataset.keyboardVisualOpen = keyboardVisualOpen ? 'true' : 'false';
        shellNode.style.setProperty('--raid-mobile-chat-shell-top', `${top}px`);
        shellNode.style.setProperty('--raid-mobile-chat-shell-height', `${height}px`);
      }

      if (!previousGate.settled && nextGate.settled) settleVersionRef.current += 1;
      const nextState = {
        keyboardOpen: nextGate.phase === 'open' || nextGate.phase === 'closing',
        keyboardVisualOpen,
        viewportSettled: nextGate.settled,
        settleVersion: settleVersionRef.current,
        keyboardPhase: nextGate.phase,
      };
      const current = keyboardStateRef.current;
      if (
        current.keyboardOpen !== nextState.keyboardOpen
        || current.keyboardVisualOpen !== nextState.keyboardVisualOpen
        || current.viewportSettled !== nextState.viewportSettled
        || current.settleVersion !== nextState.settleVersion
        || current.keyboardPhase !== nextState.keyboardPhase
      ) {
        keyboardStateRef.current = nextState;
        setKeyboardState(nextState);
      }

      if (nextGate.needsFrameSample && !frameRef.current) {
        frameRef.current = window.requestAnimationFrame(syncKeyboardState);
      }
    };

    syncKeyboardState();
    visualViewport?.addEventListener('resize', syncKeyboardState, { passive: true });
    visualViewport?.addEventListener('scroll', syncKeyboardState, { passive: true });
    window.addEventListener('resize', syncKeyboardState, { passive: true });
    document.addEventListener('focusin', syncKeyboardState, true);
    document.addEventListener('focusout', syncKeyboardState, true);

    return () => {
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
      visualViewport?.removeEventListener('resize', syncKeyboardState);
      visualViewport?.removeEventListener('scroll', syncKeyboardState);
      window.removeEventListener('resize', syncKeyboardState);
      document.removeEventListener('focusin', syncKeyboardState, true);
      document.removeEventListener('focusout', syncKeyboardState, true);
    };
  }, []);

  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  const childContent = typeof children === 'function' ? children(keyboardState) : children;
  return <StablePortal active target={portalTarget}>
    <div
      ref={shellRef}
      className={`raid-control-mobile-chat-viewport${keyboardState.keyboardOpen ? ' is-keyboard-open' : ''}`}
      data-chat-viewport-owner="visual-viewport-shell"
      data-shell-geometry-owner="visual-viewport"
      data-keyboard-phase={keyboardState.keyboardPhase}
      data-keyboard-visual-open={keyboardState.keyboardVisualOpen ? 'true' : 'false'}
      data-viewport-settled={keyboardState.viewportSettled ? 'true' : 'false'}
    >
      {toolbar}
      {childContent}
      {overlay ? <div className="raid-control-mobile-chat-overlay" data-mobile-chat-overlay-host="true">{overlay}</div> : null}
    </div>
  </StablePortal>;
}

export function RaidControlRoom({ user, unionName = '', mobile = false }) {
  const userId = String(user?.id || '');
  const room = useRaidControlRoom({ userId });
  const workspaceRef = useRef(null);
  const [actionDialog, setActionDialog] = useState(null);
  const [actionNotice, setActionNotice] = useState('');
  const [orderDialogConfig, setOrderDialogConfig] = useState({ record: null, mode: 'saved' });
  const [desktopView, setDesktopView] = useState('chat');
  const [mobileView, setMobileView] = useState('chat');
  const [mobileNoticeExpanded, setMobileNoticeExpanded] = useState(false);
  useEffect(() => { setMobileNoticeExpanded(false); }, [room.context.season?.id]);
  const [mobileBossPanelOpen, setMobileBossPanelOpen] = useState(false);
  const [mobileToolbarHost, setMobileToolbarHost] = useState(null);

  useLayoutEffect(() => {
    const nextHost = mobile ? workspaceRef.current?.closest('.mobile-main') || null : null;
    setMobileToolbarHost((current) => current === nextHost ? current : nextHost);
  }, [mobile]);


  const selectMobileView = (nextView) => {
    setMobileBossPanelOpen(false);
    setMobileView(nextView);
  };

  const selectMobileMember = (member) => {
    setActionNotice('');
    room.selectMember(member.memberOpenid);
    selectMobileView('practice');
  };

  const openActionDialog = (action) => {
    setMobileBossPanelOpen(false);
    setActionNotice('');
    setActionDialog((current) => reduceRaidControlActionState(current, { type: 'open', action }));
  };
  const closeActionDialog = () => setActionDialog((current) => reduceRaidControlActionState(current, { type: 'close' }));
  const openSelfRequest = () => openActionDialog('self-request');
  const openOrder = (record = null, mode = 'saved') => {
    if (!room.selectedMember) {
      setActionNotice('공격 지시할 멤버를 먼저 선택해주세요.');
      return false;
    }
    setOrderDialogConfig({ record, mode: record ? 'saved' : mode });
    openActionDialog('order');
    return true;
  };
  const exitControlRoom = () => {
    window.location.hash = '/raid';
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  };

  const showMobileChat = mobile && mobileView === 'chat';
  const showMobileProgress = mobile && mobileView === 'progress';
  const showMobilePlan = mobile && mobileView === 'plan';
  const mobileRoomReady = mobile && !room.loading && Boolean(room.context.season?.id);
  const mobileChatReady = showMobileChat && mobileRoomReady;
  const actionDialogContent = actionDialog === 'self-request'
    ? <MemberAttackRequestDialog room={room} userId={userId} onClose={closeActionDialog} onDone={closeActionDialog} />
    : actionDialog === 'order'
      ? <OrderDialog room={room} initialRecord={orderDialogConfig.record} initialMode={orderDialogConfig.mode} mobile={mobile} onClose={closeActionDialog} onDone={closeActionDialog} />
        : null;
  const mobileActionOverlay = mobile && (actionDialogContent || actionNotice || mobileBossPanelOpen) ? <>
    {mobileBossPanelOpen ? <MobileBossStatusPanel room={room} onClose={() => setMobileBossPanelOpen(false)} /> : null}
    {actionDialogContent}
    {actionNotice ? <div className="raid-control-mobile-action-notice" role="status"><span>{actionNotice}</span><button type="button" onClick={() => setActionNotice('')}>확인</button></div> : null}
  </> : null;
  const initialScrollKey = String(room.context.season?.id || room.context.season?.seasonNo || 'raid-control');
  const mobileToolbar = mobile ? <MobileControlToolbar
    room={room}
    unionName={unionName}
    activeView={mobileView}
    noticeExpanded={mobileNoticeExpanded}
    onToggleNotice={() => setMobileNoticeExpanded(value => !value)}
    bossPanelOpen={mobileBossPanelOpen}
    onBossPanelOpen={() => setMobileBossPanelOpen(true)}
    onBossPanelClose={() => setMobileBossPanelOpen(false)}
    onSelect={selectMobileView}
    onSelectMember={selectMobileMember}
    onExit={exitControlRoom}
    portalTarget={mobileToolbarHost}
    viewportOwned={mobileChatReady}
  /> : null;

  return <section ref={workspaceRef} className={`raid-control-workspace ${mobile ? `mobile view-${mobileView}` : 'desktop'}`}>
    <InitialChatScrollGuard rootRef={workspaceRef} active={!mobile && !showMobileProgress && !showMobilePlan} resetKey={initialScrollKey} unreadAnchorId={room.unreadAnchorId} />
    {mobileRoomReady && !mobileChatReady ? mobileToolbar : null}
    {!mobileChatReady ? <div className="raid-control-workspace-main" aria-hidden={showMobileProgress || showMobilePlan ? 'true' : undefined}>
    <LegacyRaidControlRoom
      desktopOrders={!mobile ? <RaidControlProgressPanel room={room} userId={userId} /> : null}
      room={room}
      user={user}
      mobile={mobile}
      mobilePanel={mobileView}
      onMobilePanelChange={selectMobileView}
      desktopView={desktopView}
      onDesktopViewChange={setDesktopView}
      onSelfRequestOpen={openSelfRequest}
      onOrderOpen={openOrder}
          /></div> : null}
    {mobileChatReady ? <MobileChatViewportOwner toolbar={mobileToolbar} overlay={mobileActionOverlay}>
      {({ keyboardOpen, keyboardPhase, viewportSettled, settleVersion }) => <>
        <MobileRaidControlChat
          room={room}
          userId={userId}
          noticeExpanded={mobileNoticeExpanded}
          onSelfRequestOpen={openSelfRequest}
          onOrderOpen={() => openOrder(null, 'custom')}
                    keyboardOpen={keyboardOpen}
          keyboardPhase={keyboardPhase}
          viewportSettled={viewportSettled}
          settleVersion={settleVersion}
        />
      </>}
    </MobileChatViewportOwner> : null}
    {mobileRoomReady && !mobileChatReady && mobileActionOverlay ? <MobileChatViewportOwner overlay={mobileActionOverlay} /> : null}
    {showMobilePlan ? <div className="raid-control-tetris-mobile-view"><RaidControlStrategyPanel mobile onClose={() => selectMobileView('chat')} /></div> : null}
    {(mobile && showMobileProgress) ? <RaidControlProgressPanel room={room} userId={userId} mobile={mobile} defaultExpanded={showMobileProgress || !mobile} /> : null}
    {!mobile ? actionDialogContent : null}
    {!mobile && actionNotice ? <p className="raid-control-inline-error" role="status">{actionNotice}</p> : null}
  </section>;
}
